// Central token/session storage.
//
// Persistence: localStorage (NOT sessionStorage) so a logged-in session survives page reloads,
// new tabs, and browser restarts. sessionStorage is scoped to a single tab and is cleared when the
// tab closes, which is why opening the base URL in a new tab used to fall through to the login page.
const store = window.localStorage;

// Setters and Getters for Access Token
export function setAccessToken(aToken) {
  store.setItem("accessToken", aToken);
}
export function getAccessToken() {
  return store.getItem("accessToken");
}
export function clearAccessToken() {
  store.removeItem("accessToken");
}

// Setters and Getters for Refresh Token
export function setRefreshToken(rToken) {
  store.setItem("refreshToken", rToken);
}
export function getRefreshToken() {
  return store.getItem("refreshToken");
}
export function clearRefreshToken() {
  store.removeItem("refreshToken");
}

// Setters and Getters for guId
export function setGuId(guid) {
  store.setItem("guid", guid);
  store.setItem("guId", guid);
}
export function getGuId() {
  return store.getItem("guid") || store.getItem("guId");
}
export function clearGuId() {
  store.removeItem("guid");
  store.removeItem("guId");
}

// Setters and Getters for loginid
export function setLoginId(loginId) {
  store.setItem("loginid", loginId);
  store.setItem("id", loginId);
}
export function getLoginId() {
  return store.getItem("loginid") || store.getItem("id");
}
export function clearLoginId() {
  store.removeItem("loginid");
  store.removeItem("id");
}

// Setters and Getters for First Name
export function setFirstname(firstname) {
  store.setItem("firstname", firstname);
}
export function getFirstname() {
  return store.getItem("firstname");
}
export function clearFirstname() {
  store.removeItem("firstname");
}

// Setters and Getters for Last Name
export function setLastname(lastname) {
  store.setItem("lastname", lastname);
}
export function getLastname() {
  return store.getItem("lastname");
}
export function clearLastname() {
  store.removeItem("lastname");
}
// Setters and Getters for Email
export function setEmailID(email) {
  store.setItem("email", email);
}
export function getEmailID() {
  return store.getItem("email");
}
export function clearEmailID() {
  store.removeItem("email");
}

// Setters and Getters for MakeInvitations
export function setMakeInvitations(makeInvitations) {
  store.setItem("MakeInvitations", String(makeInvitations));
}
export function getMakeInvitations() {
  return store.getItem("MakeInvitations");
}
export function clearMakeInvitations() {
  store.removeItem("MakeInvitations");
}

// Setters and Getters for Language
export function setLanguage(language) {
  store.setItem("language", language);
}
export function getLanguage() {
  return store.getItem("language");
}
export function clearLanguage() {
  store.removeItem("language");
}

const SIDEBAR_COLLAPSED_KEY = "sidebarCollapsed";
const SSO_LOGIN_KEY = "isSSOLogin";

export function setSidebarCollapsed(isCollapsed) {
  store.setItem(SIDEBAR_COLLAPSED_KEY, String(Boolean(isCollapsed)));
}

export function getSidebarCollapsed() {
  return store.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
}

export function clearSidebarCollapsed() {
  store.removeItem(SIDEBAR_COLLAPSED_KEY);
}

// Setters and Getters for SSO login state
export function setSSOLogin(isSSOLogin) {
  store.setItem(SSO_LOGIN_KEY, String(Number(Boolean(isSSOLogin))));
}

export function getSSOLogin() {
  return store.getItem(SSO_LOGIN_KEY) === "1";
}

export function clearSSOLogin() {
  store.removeItem(SSO_LOGIN_KEY);
}

// Persist a full auth session in one place (shared by the login paths that receive a complete
// user record: manual login + Keycloak). Callers pass already-resolved values — each path
// resolves field names / defaults from its own response shape — and every field is written
// exactly as passed. `makeInvitations` is only written when provided (some paths omit it).
export function persistSession({
  isSSOLogin,
  accessToken,
  refreshToken,
  guid,
  loginId,
  firstName,
  lastName,
  email,
  makeInvitations,
} = {}) {
  setSSOLogin(isSSOLogin);
  setAccessToken(accessToken);
  setRefreshToken(refreshToken);
  setGuId(guid);
  setLoginId(loginId);
  setFirstname(firstName);
  setLastname(lastName);
  setEmailID(email);
  if (makeInvitations !== undefined && makeInvitations !== null) {
    setMakeInvitations(makeInvitations);
  }
}

// Write the profile fields carried by a Profile_api Records object into the session store.
// Shared by the login paths that hydrate profile from a follow-up Profile call (SSO query-param
// via LoginEvent, and AutoLogin). Each field is written only when present, matching prior
// behavior. Callers keep their own React `setUser(...)` (component state); LoginEvent also keeps
// its `setMakeInvitations(...)` (AutoLogin never wrote it).
export function hydrateProfileFromRecords(records = {}) {
  if (records?.FirstName) setFirstname(records.FirstName);
  if (records?.LastName) setLastname(records.LastName);
  if (records?.PrimaryEmail) setEmailID(records.PrimaryEmail);

  const loginId = records?.Id || records?.id;
  if (loginId) setLoginId(loginId);

  if (records?.Notifications !== undefined && records?.Notifications !== null) {
    store.setItem("Notifications", records.Notifications);
  }
}
